// Utilities for sharing project layouts via URL

/**
 * Encodes project data into a URL-safe string
 * @param {Object} projectData - The project data to encode
 * @returns {string} - Base64 encoded and compressed string
 */
export const encodeProjectData = (projectData) => {
  try {
    // Remove sensitive data and clean up the data
    const cleanData = {
      title: projectData.title,
      description: projectData.description,
      icon: projectData.icon,
      tags: projectData.tags,
      components: projectData.components?.map(component => ({
        id: component.id,
        type: component.type,
        title: component.title,
        data: component.data,
        position: component.position
      }))
    };

    // Convert to JSON and encode
    const jsonString = JSON.stringify(cleanData);
    const base64 = btoa(unescape(encodeURIComponent(jsonString)));
    return base64;
  } catch (error) {
    console.error('Error encoding project data:', error);
    throw new Error('Failed to encode project data for sharing');
  }
};

/**
 * Decodes project data from a URL-safe string
 * @param {string} encodedData - The encoded project data
 * @returns {Object} - Decoded project data
 */
export const decodeProjectData = (encodedData) => {
  try {
    const jsonString = decodeURIComponent(escape(atob(encodedData)));
    const projectData = JSON.parse(jsonString);
    
    // Validate the decoded data structure
    if (!projectData.title || !Array.isArray(projectData.components)) {
      throw new Error('Invalid project data structure');
    }
    
    return projectData;
  } catch (error) {
    console.error('Error decoding project data:', error);
    throw new Error('Invalid or corrupted shared project data');
  }
};

/**
 * Generates a shareable URL for a project
 * @param {Object} projectData - The project data
 * @returns {string} - Complete shareable URL
 */
export const generateShareUrl = (projectData) => {
  try {
    const encodedData = encodeProjectData(projectData);
    const baseUrl = window.location.origin;
    return `${baseUrl}/shared?data=${encodedData}`;
  } catch (error) {
    console.error('Error generating share URL:', error);
    throw new Error('Failed to generate shareable URL');
  }
};

/**
 * Extracts project data from the current URL
 * @returns {Object|null} - Decoded project data or null if not found
 */
export const getSharedProjectFromUrl = () => {
  try {
    const urlParams = new URLSearchParams(window.location.search);
    const encodedData = urlParams.get('data');
    
    if (!encodedData) {
      return null;
    }
    
    return decodeProjectData(encodedData);
  } catch (error) {
    console.error('Error extracting shared project from URL:', error);
    return null;
  }
};

/**
 * Validates if the current URL contains valid shared project data
 * @returns {boolean} - True if valid shared data exists
 */
export const hasValidSharedData = () => {
  try {
    const sharedData = getSharedProjectFromUrl();
    return sharedData !== null;
  } catch (error) {
    return false;
  }
};

/**
 * Creates a summary of the shared project for display
 * @param {Object} projectData - The project data
 * @returns {Object} - Summary information
 */
export const getProjectSummary = (projectData) => {
  if (!projectData) return null;
  
  const componentCounts = {};
  projectData.components?.forEach(component => {
    componentCounts[component.type] = (componentCounts[component.type] || 0) + 1;
  });
  
  return {
    title: projectData.title,
    description: projectData.description,
    icon: projectData.icon,
    totalComponents: projectData.components?.length || 0,
    componentTypes: Object.keys(componentCounts).length,
    componentCounts,
    tags: projectData.tags || []
  };
};
